import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  Customized,
} from 'recharts';
import { DocumentIcon } from '@heroicons/react/24/solid';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

// Custom shape for waterfall bars
const CustomBarShape = ({ x, y, width, height, payload, fill }) => {
  if (height <= 0) return null;

  let radius = 8;
  const isDiff = payload.name === 'Diff.';
  const right = x + width;
  const bottom = y + height;

  if (height < radius * 2) {
    radius = height / 2;
  }

  let path = '';

  if (isDiff) {
    path = `
      M${x},${y + radius}
      A${radius},${radius} 0 0 1 ${x + radius},${y}
      L${right - radius},${y}
      A${radius},${radius} 0 0 1 ${right},${y + radius}
      L${right},${bottom - radius}
      A${radius},${radius} 0 0 1 ${right - radius},${bottom}
      L${x + radius},${bottom}
      A${radius},${radius} 0 0 1 ${x},${bottom - radius}
      Z
    `;
  } else {
    path = `
      M${x},${y + radius}
      A${radius},${radius} 0 0 1 ${x + radius},${y}
      L${right - radius},${y}
      A${radius},${radius} 0 0 1 ${right},${y + radius}
      L${right},${bottom}
      L${x},${bottom}
      Z
    `;
  }

  return <path d={path} fill={fill} />;
};

const WaterfallLines = ({ yAxisMap, xAxisMap, offset, inValue, outValue, diffBase, diffValue }) => {
  const yScale = yAxisMap[0].scale;
  const xScale = xAxisMap[0].scale;

  const inX = xScale('In') + offset.left + xScale.bandwidth() / 2;
  const outX = xScale('Out') + offset.left + xScale.bandwidth() / 2;
  const diffX = xScale('Diff.') + offset.left + xScale.bandwidth() / 2;

  const inTop = yScale(inValue);
  const outTop = yScale(outValue);
  const diffTop = yScale(diffBase + diffValue);
  const diffBaseY = yScale(diffBase);

  const isInGreater = inValue > outValue;

  return (
    <g>
      <line
        x1={inX}
        y1={inTop}
        x2={diffX}
        y2={isInGreater ? diffTop : diffBaseY}
        stroke="#bbb"
        strokeDasharray="4 2"
        strokeWidth={1}
      />
      <line
        x1={outX}
        y1={outTop}
        x2={diffX}
        y2={isInGreater ? diffBaseY : diffTop}
        stroke="#999"
        strokeDasharray="4 2"
        strokeWidth={1}
      />
    </g>
  );
};

const formatValue = (value) => {
  return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(1);
};

const ChartWaterfall = ({
  flows_values,
  selectedFlowCategL0,
  first_date,
  last_date,
  qty_days,
  loading = false
}) => {
  let currentFlow;
  if (selectedFlowCategL0 === 'All' && flows_values) {
    let totalIn = 0;
    let totalOut = 0;
    Object.values(flows_values).forEach(val => {
      totalIn += Number(val.In) || 0;
      totalOut += Number(val.Out) || 0;
    });
    currentFlow = { In: totalIn, Out: totalOut };
  } else {
    currentFlow = flows_values?.[selectedFlowCategL0] || { In: 0, Out: 0 };
  }
  const inValue = currentFlow.In;
  const outValue = currentFlow.Out * -1;
  const diff = Number(Math.abs(inValue - outValue).toFixed(2));
  const diffBase = Math.min(inValue, outValue);
  const isInGreater = inValue > outValue;
  const diffColor = isInGreater ? '#AFDC7E' : '#FFA3A3';
  const data = [
    { name: 'In', value: inValue, base: 0, color: '#166534' },
    { name: 'Out', value: outValue, base: 0, color: '#C00000' },
    { name: 'Diff.', value: diff, base: diffBase, color: diffColor }
  ];

  const displayedFirstDate = first_date || '-';
  const displayedLastDate = last_date || '-';
  const displayedQtyDays = qty_days != null ? qty_days : '-';

  // ###### LOADING AND EMPTY STATES
  const hasData = inValue !== 0 || outValue !== 0;

  if (loading) {
    return (
      <div className="flex flex-col">
        <div className="h-56 bg-white shadow-lg rounded-xl p-3 relative overflow-hidden">
          <div className="h-6 bg-gray-400 rounded animate-pulse w-64 mb-2"></div>
          <div className="flex h-full items-center">
            {/* Left side skeleton */}
            <div className="flex flex-col justify-center mr-4 w-24">
              <div className="space-y-3">
                <div className="h-3 bg-gray-400 rounded animate-pulse w-20"></div>
                <div className="h-4 bg-gray-400 rounded animate-pulse w-16"></div>
                <div className="h-3 bg-gray-400 rounded animate-pulse w-18"></div>
                <div className="h-4 bg-gray-400 rounded animate-pulse w-14"></div>
              </div>
            </div>
            {/* Right side skeleton */}
            <div className="flex-1 h-full flex items-center justify-around">
              <div className="w-12 bg-gray-400 rounded-t animate-pulse" style={{ height: '50px' }}></div>
              <div className="w-12 bg-gray-400 rounded-t animate-pulse" style={{ height: '35px' }}></div>
              <div className="w-12 bg-gray-400 rounded-t animate-pulse" style={{ height: '65px' }}></div>
            </div>
          </div>
          
          {/* Spinner overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
            <SpinnerLoad />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="h-56 bg-white shadow-lg rounded-xl p-3">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-md font-semibold">Flow Consolidated</span>
          <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
            {selectedFlowCategL0}
          </span>
        </div>
        {hasData ? (
          <div className="flex h-full items-center">
            <div className="flex flex-col justify-center mr-4 w-24">
              <div className="mb-2">
                <div className="text-xs text-gray-500">First</div>
                <div className="text-sm font-semibold">{displayedFirstDate}</div>
              </div>
              <div className="mb-2">
                <div className="text-xs text-gray-500">Last</div>
                <div className="text-sm font-semibold">{displayedLastDate}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Qty Days</div>
                <div className="text-sm font-semibold">{displayedQtyDays}</div>
              </div>
            </div>
            <div className="flex-1 h-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 14, fontWeight: 'bold', fill: '#444' }} />
                  <YAxis hide />
                  <Tooltip />
                  <Customized
                    component={(props) => (
                      <WaterfallLines
                        {...props}
                        inValue={inValue}
                        outValue={outValue}
                        diffBase={diffBase}
                        diffValue={diff}
                      />
                    )}
                  />
                  <Bar dataKey="base" stackId="a" fill="transparent" />
                  <Bar dataKey="value" stackId="a" shape={(props) => <CustomBarShape {...props} />}>
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="top"
                      fontSize={14}
                      fill="#444"
                      fontWeight="bold"
                      formatter={formatValue}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-400 mb-3">
                <DocumentIcon className="w-16 h-16 mx-auto" />
              </div>
              <div className="text-gray-500 text-sm font-medium mb-2">
                No flow data to show
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartWaterfall;
